import axios, { AxiosInstance } from 'axios';
import {
  ApiResponse,
  DtpCatalogResponse,
  DtpCreateOrderResponse,
  DtpSignupRequest,
  DtpMembershipResponse,
  Patient,
  Order
} from '@bloom/shared';
import { useDtpAuthStore } from '../store/useDtpAuthStore';
import { getClinicSite } from './clinicSite';
import { analyticsHeaders } from './analytics';
import {
  LabResultDetail,
  LabResultLinkContext,
  LabResultSummary
} from '../types/labResults';
import type { CareMessage, CareThreadPage, PatientTreatment, ProgramDoseChoices } from '../types/careMessaging';
import type {
  PortalRecommendation,
  RefillEligibility,
  RefillIntervalDays,
  RefillPlan,
  RefillPlansResponse
} from '../types/refills';

const CATALOG_TTL_MS = 60_000;

export interface SignInMethods {
  /** False when this session didn't prove the phone (e.g. it came from an emailed link). */
  canManage?: boolean;
  hasPassword: boolean;
  passwordSetAt?: string;
  passkeys: { id: string; deviceName: string; createdAt: string; lastUsedAt?: string }[];
}

class DtpApiClient {
  private client: AxiosInstance;

  constructor() {
    const baseURL = import.meta.env.VITE_API_URL || '/api';

    this.client = axios.create({
      baseURL: `${baseURL}/dtp`,
      timeout: 15000,
      headers: { 'Content-Type': 'application/json' }
    });

    // Add patient auth token to requests
    this.client.interceptors.request.use((config) => {
      if (getClinicSite()) config.baseURL = '/api/dtp';
      const token = localStorage.getItem('dtp-token');
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      // So server-recorded outcomes (orders, sign-ups) land on this visit's analytics session.
      Object.assign(config.headers, analyticsHeaders());
      return config;
    });

    // On 401 responses, clear auth so the patient is redirected to re-authenticate.
    // Cart state (separate store) is preserved so they don't lose progress.
    this.client.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          useDtpAuthStore.getState().logout();
        }
        return Promise.reject(error);
      }
    );
  }

  // --- Auth ---

  async requestOtp(phone: string, physicianId: string): Promise<ApiResponse> {
    const { data } = await this.client.post('/auth/request-otp', { phone, physicianId });
    return data;
  }

  async verifyOtp(phone: string, code: string, physicianId: string): Promise<ApiResponse<{
    token: string;
    /** Present for a returning patient (isNewPatient false). */
    patient?: Patient;
    isNewPatient: boolean;
  }>> {
    const { data } = await this.client.post('/auth/verify-otp', { phone, code, physicianId });
    return data;
  }

  // Auto-import: mint a patient session from an HMAC-signed link (no OTP).
  // The clinic's system signs the params; here we just forward them.
  async autoImport(physicianId: string, params: Record<string, string>): Promise<ApiResponse<{
    token: string;
    patient: Patient;
    isNewPatient: boolean;
  }>> {
    const { data } = await this.client.post('/auth/import', { physicianId, ...params });
    return data;
  }

  /** Spend a single-use handoff code from the clinic portal for a session. */
  async exchangeImpersonationCode(code: string): Promise<ApiResponse<{
    token: string;
    patient: Patient;
    physicianId: string;
    impersonated: boolean;
  }>> {
    const { data } = await this.client.post('/auth/impersonation-exchange', { code });
    return data;
  }

  // --- Signup ---

  async signup(signupToken: string, body: DtpSignupRequest): Promise<ApiResponse<{
    token: string;
    patient: Patient;
  }>> {
    const { data } = await this.client.post('/signup', {
      token: signupToken,
      ...body
    });
    return data;
  }

  // --- Catalog ---

  // Store pages each load the catalog on mount; share one response across a
  // browsing session instead of refetching the whole catalog per navigation.
  private catalogCache = new Map<string, { at: number; response: Promise<ApiResponse<DtpCatalogResponse>>; data?: ApiResponse<DtpCatalogResponse> }>();

  /**
   * Cached for CATALOG_TTL_MS; concurrent callers share the in-flight request.
   * Pass `fresh` where stale data would matter (checkout, which the server
   * re-validates anyway).
   */
  getCatalog(physicianId: string, { fresh = false } = {}): Promise<ApiResponse<DtpCatalogResponse>> {
    const cached = this.catalogCache.get(physicianId);
    if (!fresh && cached && Date.now() - cached.at < CATALOG_TTL_MS) return cached.response;
    const entry: { at: number; response: Promise<ApiResponse<DtpCatalogResponse>>; data?: ApiResponse<DtpCatalogResponse> } = {
      at: Date.now(),
      response: this.client.get(`/catalog/${physicianId}`).then(({ data }) => {
        if (data?.success) entry.data = data;
        else if (this.catalogCache.get(physicianId) === entry) this.catalogCache.delete(physicianId);
        return data;
      }, (error) => {
        if (this.catalogCache.get(physicianId) === entry) this.catalogCache.delete(physicianId);
        throw error;
      }),
    };
    this.catalogCache.set(physicianId, entry);
    return entry.response;
  }

  /** The cached catalog if already loaded and fresh, for rendering without a loading flash. */
  peekCatalog(physicianId: string): ApiResponse<DtpCatalogResponse> | undefined {
    const cached = this.catalogCache.get(physicianId);
    return cached && Date.now() - cached.at < CATALOG_TTL_MS ? cached.data : undefined;
  }

  // --- Screening ---

  async updateScreening(answers: Record<string, any>): Promise<ApiResponse> {
    const { data } = await this.client.put('/screening', answers);
    return data;
  }

  // --- Orders ---

  async createOrder(
    items: { productId: string }[],
    shippingAddress?: { street: string; street2?: string; city: string; state: string; zipCode: string },
    billingAddress?: { street: string; street2?: string; city: string; state: string; zipCode: string },
    consents?: string[],
    qualificationAnswers?: import('@bloom/shared').ProductQualificationSubmission[],
    programAssessments?: import('@bloom/shared').IntakeProgramSubmission[]
  ): Promise<ApiResponse<DtpCreateOrderResponse>> {
    const { data } = await this.client.post('/orders', { items, shippingAddress, billingAddress, consents, qualificationAnswers, programAssessments });
    return data;
  }

  async evaluateProgram(physicianId: string, programId: string, submission: import('@bloom/shared').IntakeProgramSubmission) {
    const { data } = await this.client.post(`/intake-programs/${physicianId}/${programId}/evaluate`, submission);
    return data;
  }

  async getOrders(): Promise<ApiResponse<Order[]>> {
    const { data } = await this.client.get('/orders');
    return data;
  }

  async getOrder(orderId: string): Promise<ApiResponse<Order>> {
    const { data } = await this.client.get(`/orders/${orderId}`);
    return data;
  }

  async postAnalytics(body: Record<string, any>): Promise<ApiResponse> {
    const { data } = await this.client.post('/analytics', body);
    return data;
  }

  // --- Membership ---

  async getMyMembership(): Promise<{ success: boolean; membership: DtpMembershipResponse | null; error?: string }> {
    const { data } = await this.client.get('/memberships/me');
    return data;
  }

  async createMembership(body: {
    tier: 1 | 2 | 3;
    selectedProductIds: string[];
    selectedGoals?: string[];
    cycleLengthDays?: number;
  }): Promise<{
    success: boolean;
    membership?: DtpMembershipResponse;
    setupClientSecret?: string;
    setupPaymentId?: string;
    // Typed error from the profile gate. When present the FE renders an
    // inline form for the listed fields instead of a generic error banner.
    code?: 'profile_incomplete';
    missingFields?: Array<'email' | 'firstName' | 'lastName' | 'dateOfBirth' | 'address'>;
    error?: string;
  }> {
    try {
      const { data } = await this.client.post('/memberships', body);
      return data;
    } catch (err: any) {
      // Profile-incomplete responses come back as 400 — surface the body
      // directly so the caller can branch on `code` / `missingFields`.
      const body = err?.response?.data;
      if (body && body.code === 'profile_incomplete') {
        return body;
      }
      throw err;
    }
  }

  async swapMembershipSelection(productIds: string[]): Promise<ApiResponse<{ membership: DtpMembershipResponse }>> {
    const { data } = await this.client.post('/memberships/me/selection', { productIds });
    return data;
  }

  async pauseMembership(): Promise<ApiResponse<{ membership: DtpMembershipResponse }>> {
    const { data } = await this.client.post('/memberships/me/pause');
    return data;
  }

  async resumeMembership(): Promise<ApiResponse<{ membership: DtpMembershipResponse }>> {
    const { data } = await this.client.post('/memberships/me/resume');
    return data;
  }

  async cancelMembership(reason?: string): Promise<ApiResponse<{ membership: DtpMembershipResponse }>> {
    const { data } = await this.client.post('/memberships/me/cancel', { reason });
    return data;
  }

  async startReplacePaymentMethod(): Promise<ApiResponse<{ clientSecret: string; paymentId: string }>> {
    const { data } = await this.client.post('/memberships/me/payment-method');
    return data;
  }

  async changeMembershipTier(body: { tier: 1 | 2 | 3; selectedProductIds: string[] }): Promise<{ success: boolean; membership?: any; error?: string }> {
    const { data } = await this.client.post('/memberships/me/tier', body);
    return data;
  }

  async skipNextCycle(): Promise<{ success: boolean; membership?: any; error?: string }> {
    const { data } = await this.client.post('/memberships/me/skip-cycle');
    return data;
  }

  async updateMembershipGoals(goals: string[]): Promise<{ success: boolean; membership?: any; error?: string }> {
    const { data } = await this.client.post('/memberships/me/goals', { goals });
    return data;
  }

  async updateMembershipNotifications(prefs: { sms?: boolean; renewalReminder?: boolean }): Promise<{ success: boolean; membership?: any; error?: string }> {
    const { data } = await this.client.put('/memberships/me/notifications', prefs);
    return data;
  }

  async getMembershipCycles(): Promise<{ success: boolean; cycles: any[]; error?: string }> {
    const { data } = await this.client.get('/memberships/me/cycles');
    return data;
  }

  async getMembershipOrder(orderId: string): Promise<{ success: boolean; order?: any; invoice?: any; error?: string }> {
    const { data } = await this.client.get(`/memberships/me/orders/${orderId}`);
    return data;
  }

  // --- Patient profile ---

  async getPatientProfile(): Promise<{ success: boolean; patient: any; error?: string }> {
    const { data } = await this.client.get('/patient/me');
    return data;
  }

  // --- Sign-in methods (password, passkeys) for the signed-in patient ---

  async getSignInMethods(): Promise<SignInMethods> {
    const { data } = await this.client.get('/login/methods');
    return data.data;
  }

  async setPassword(password: string): Promise<void> {
    await this.client.put('/login/methods/password', { password });
  }

  async removePassword(): Promise<void> {
    await this.client.delete('/login/methods/password');
  }

  async passkeyRegistrationOptions(): Promise<{ challengeId: string; options: any }> {
    const { data } = await this.client.post('/login/methods/passkeys/options', {});
    return data.data;
  }

  async registerPasskey(challengeId: string, response: unknown, deviceName?: string): Promise<SignInMethods> {
    const { data } = await this.client.post('/login/methods/passkeys', { challengeId, response, deviceName });
    return data.data;
  }

  async removePasskey(credentialId: string): Promise<void> {
    await this.client.delete(`/login/methods/passkeys/${encodeURIComponent(credentialId)}`);
  }

  async updatePatientAddress(body: {
    street: string;
    street2?: string;
    city: string;
    state: string;
    zipCode: string;
    country?: string;
  }): Promise<{ success: boolean; address?: any; error?: string }> {
    const { data } = await this.client.put('/patient/me/address', body);
    return data;
  }

  async updatePatientEmail(email: string): Promise<{ success: boolean; email?: string; error?: string }> {
    const { data } = await this.client.put('/patient/me/email', { email });
    return data;
  }

  async updatePatientProfile(body: {
    email?: string;
    firstName?: string;
    lastName?: string;
    dateOfBirth?: string;
    address?: {
      street: string;
      street2?: string;
      city: string;
      state: string;
      zipCode: string;
      country?: string;
    };
  }): Promise<{ success: boolean; patient?: any; error?: string }> {
    const { data } = await this.client.put('/patient/me/profile', body);
    return data;
  }

  // --- Lab results ---

  /**
   * Public: resolves the token from a results SMS into the clinic and result it
   * points at, so the sign-in screen can address the right clinic. Returns null
   * data for an expired or tampered link, which the caller treats as "just sign
   * in normally" rather than as an error.
   */
  async getLabResultLinkContext(token: string): Promise<ApiResponse<LabResultLinkContext | null>> {
    const { data } = await this.client.post('/lab-results/link-context', { token });
    return data;
  }

  async getLabResults(): Promise<ApiResponse<LabResultSummary[]>> {
    const { data } = await this.client.get('/lab-results');
    return data;
  }

  async getLabResult(resultId: string, signal?: AbortSignal): Promise<ApiResponse<LabResultDetail>> {
    const { data } = await this.client.get(`/lab-results/${resultId}`, { signal });
    return data;
  }

  async getLabResultAttachmentUrl(
    resultId: string,
    index: number
  ): Promise<ApiResponse<{ url: string; fileName?: string }>> {
    const { data } = await this.client.get(`/lab-results/${resultId}/attachments/${index}`);
    return data;
  }

  // --- Self-service: saved cards, receipts, notification preferences, lab orders ---
  // (backend routes/dtp/selfService.ts). These throw on a refused request; use
  // selfServiceError() from lib/patientSelfService for the server's message.

  async getPaymentMethods(): Promise<import('./patientSelfService').PatientCards> {
    const { data } = await this.client.get('/billing/payment-methods');
    return data.data;
  }

  /** Client secret for the Hyperswitch SDK's save-card form. */
  async startAddPaymentMethod(): Promise<{ clientSecret: string }> {
    const { data } = await this.client.post('/billing/payment-methods/setup', {});
    return data.data;
  }

  async removePaymentMethod(cardId: string): Promise<void> {
    await this.client.delete(`/billing/payment-methods/${encodeURIComponent(cardId)}`);
  }

  async setDefaultPaymentMethod(cardId: string): Promise<void> {
    await this.client.post(`/billing/payment-methods/${encodeURIComponent(cardId)}/default`, {});
  }

  async getReceipts(): Promise<import('./patientSelfService').ReceiptSummary[]> {
    const { data } = await this.client.get('/billing/receipts');
    return data.data;
  }

  async getReceipt(receiptId: string): Promise<import('./patientSelfService').ReceiptDetail> {
    const { data } = await this.client.get(`/billing/receipts/${encodeURIComponent(receiptId)}`);
    return data.data;
  }

  async emailReceipt(receiptId: string): Promise<{ sentTo: string }> {
    const { data } = await this.client.post(`/billing/receipts/${encodeURIComponent(receiptId)}/email`, {});
    return data.data;
  }

  async getNotificationPreferences(): Promise<import('./patientSelfService').NotificationSettings> {
    const { data } = await this.client.get('/notifications');
    return data.data;
  }

  async updateNotificationPreferences(prefs: Partial<Record<import('./patientSelfService').NotificationCategory, Partial<Record<import('./patientSelfService').NotificationChannel, boolean>>>>): Promise<import('./patientSelfService').NotificationSettings> {
    const { data } = await this.client.put('/notifications', { prefs });
    return data.data;
  }

  async setTextsOptedOut(optedOut: boolean): Promise<import('./patientSelfService').NotificationSettings> {
    const { data } = await this.client.put('/notifications/sms', { optedOut });
    return data.data;
  }

  async getLabOrders(): Promise<import('./patientSelfService').PatientLabOrder[]> {
    const { data } = await this.client.get('/lab-orders');
    return data.data;
  }

  // --- Care team messaging, My treatments, refill requests ---

  async getCareMessages(before?: number): Promise<ApiResponse<CareThreadPage>> {
    const { data } = await this.client.get('/messages', { params: before ? { before } : undefined });
    return data;
  }

  async getCareUnreadCount(): Promise<ApiResponse<{ count: number }>> {
    const { data } = await this.client.get('/messages/unread-count');
    return data;
  }

  async sendCareMessage(body: string): Promise<ApiResponse<CareMessage>> {
    const { data } = await this.client.post('/messages', { body });
    return data;
  }

  /** Tell the clinic the patient has seen the thread up to `upToSeq`. */
  async markCareMessagesRead(upToSeq: number): Promise<ApiResponse> {
    const { data } = await this.client.post('/messages/read', { upToSeq });
    return data;
  }

  async getTreatments(): Promise<ApiResponse<PatientTreatment[]>> {
    const { data } = await this.client.get('/treatments');
    return data;
  }

  async requestRefill(treatmentKey: string, note?: string): Promise<ApiResponse<CareMessage>> {
    const { data } = await this.client.post('/treatments/refill-requests', { treatmentKey, note });
    return data;
  }

  /** The patient's program doses and how they can move to each one. */
  async getDoseChoices(): Promise<ApiResponse<ProgramDoseChoices[]>> {
    const { data } = await this.client.get('/dose-changes');
    return data;
  }

  /** Ask the clinic to approve a dose more than one step from the current one. */
  async requestDoseChange(programId: string, doseLabel: string, note?: string): Promise<ApiResponse<CareMessage>> {
    const { data } = await this.client.post('/dose-changes', { programId, doseLabel, note });
    return data;
  }

  // --- Auto-refill plans and recommendations (refills track) ---
  // Plans prepare an order awaiting payment and text a payment link; nothing
  // is charged automatically. Every mutation is refused for impersonated sessions.

  async getRefillPlans(): Promise<ApiResponse<RefillPlansResponse>> {
    const { data } = await this.client.get('/refills');
    return data;
  }

  /** Which of my received orders can be put on auto-refill, keyed by order id. */
  async getRefillEligibility(): Promise<ApiResponse<Record<string, RefillEligibility>>> {
    const { data } = await this.client.get('/refills/eligibility');
    return data;
  }

  async createRefillPlan(body: { sourceOrderId: string; intervalDays: RefillIntervalDays; consents: string[] }): Promise<ApiResponse<RefillPlan>> {
    const { data } = await this.client.post('/refills', body);
    return data;
  }

  async pauseRefillPlan(planId: string): Promise<ApiResponse<RefillPlan>> {
    const { data } = await this.client.post(`/refills/${planId}/pause`);
    return data;
  }

  /** `consents` only when the ones on file no longer cover an order (code consent_needed). */
  async resumeRefillPlan(planId: string, consents?: string[]): Promise<ApiResponse<RefillPlan>> {
    const { data } = await this.client.post(`/refills/${planId}/resume`, consents ? { consents } : {});
    return data;
  }

  async skipNextRefill(planId: string): Promise<ApiResponse<RefillPlan>> {
    const { data } = await this.client.post(`/refills/${planId}/skip`);
    return data;
  }

  async setRefillInterval(planId: string, intervalDays: RefillIntervalDays): Promise<ApiResponse<RefillPlan>> {
    const { data } = await this.client.patch(`/refills/${planId}`, { intervalDays });
    return data;
  }

  async cancelRefillPlan(planId: string): Promise<ApiResponse<RefillPlan>> {
    const { data } = await this.client.post(`/refills/${planId}/cancel`);
    return data;
  }

  async getRecommendations(): Promise<ApiResponse<{ items: PortalRecommendation[] }>> {
    const { data } = await this.client.get('/recommendations');
    return data;
  }

  // --- Support chat (Zendesk) ---

  /** The messenger key and a signed identity; `available: false` when this deploy has no support chat. */
  async getSupportIdentity(): Promise<{ available: boolean; widgetKey?: string; token?: string; tags?: string[] }> {
    const { data } = await this.client.get('/support/identity');
    return data.data;
  }
}

export const dtpApi = new DtpApiClient();
